import type { IncomingMessage } from 'http';
import type { Maybe } from 'true-myth';
import { resolveForwardableClientIp } from '@zg-rentals/ip-blocks';

type RequestLike = Pick<IncomingMessage, 'headers' | 'socket'>;

export type ServerRequestContextValue = {
  cookieHeader?: string;
  ip?: string;
  pathname?: string | null;
  requestId?: string;
  userAgent?: string;
};

type ServerRequestContextStore = {
  getStore(): ServerRequestContextValue | undefined;
  run<T>(store: ServerRequestContextValue, callback: () => T): T;
};

function createServerRequestContextStore(): ServerRequestContextStore | undefined {
  if (typeof window !== 'undefined') {
    return undefined;
  }

  try {
    // Lazy-load Node's async_hooks so this shared client module can still compile for the browser bundle.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { AsyncLocalStorage } = require('async_hooks') as typeof import('async_hooks');

    return new AsyncLocalStorage<ServerRequestContextValue>();
  } catch {
    return undefined;
  }
}

let serverRequestContext: ServerRequestContextStore | undefined;

function getServerRequestContextStore(): ServerRequestContextStore | undefined {
  if (serverRequestContext) {
    return serverRequestContext;
  }

  serverRequestContext = createServerRequestContextStore();
  return serverRequestContext;
}

function getHeaderValue(value: string | Array<string> | undefined): string {
  if (Array.isArray(value)) {
    return value[0] || '';
  }

  return value || '';
}

// Loopback/private fallbacks are a dev artifact, not a real client IP. Sending them
// upstream can cause personalized endpoints like HomeHub to return empty data, so this
// uses the same "safe to forward" resolution (trust walk from the socket peer, shared
// with the App Router helpers in @zg-rentals/next) rather than the raw client IP.
function getRequestIp(req: RequestLike): Maybe<string> {
  return resolveForwardableClientIp({
    forwardedFor: req.headers['x-forwarded-for'],
    remoteAddress: req.socket?.remoteAddress,
  });
}

export function getServerRequestContext(): ServerRequestContextValue | undefined {
  return getServerRequestContextStore()?.getStore();
}

export function runWithServerRequestContext<T>(req: RequestLike | undefined, callback: () => T): T {
  const requestContextStore = getServerRequestContextStore();

  if (!req || !requestContextStore) {
    return callback();
  }

  return requestContextStore.run(
    {
      cookieHeader: getHeaderValue(req.headers.cookie) || undefined,
      ip: getRequestIp(req).unwrapOr(undefined),
      requestId: getHeaderValue(req.headers['x-request-id']) || undefined,
      userAgent: getHeaderValue(req.headers['user-agent']),
    },
    callback,
  );
}

export function runWithServerRequestContextValue<T>(
  value: ServerRequestContextValue,
  callback: () => T,
): T {
  const store = getServerRequestContextStore();
  if (!store) return callback();
  return store.run(value, callback);
}
